/**
 * Assessment reviews side panel — the question-anchored analogue of the video
 * ReviewsPanel. Mounted in the AssessmentWorkspaceShell right panel.
 *
 * - Filter pills (Open / Queued / Sent)
 * - Cards grouped by reviewer; each comment anchors to a question (jump-to-question
 *   instead of player seek)
 * - "Add to chat" enqueues into the shared reviewQueueStore with a question anchor
 *   and sets status=accepted; the chip stack above the composer dispatches to Ivy.
 */

import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Inbox, MessageSquarePlus, RefreshCw, Trash2, X, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useReviewQueueStore } from '../../../stores/reviewQueueStore';
import { ReviewerAvatar } from '../../video-workspace/reviews/ReviewerAvatar';
import { relativeTime } from '../../video-workspace/reviews/format';
import { useAssessmentReviews } from './useAssessmentReviews';
import type { AssessmentOwnerReview, AssessmentOwnerReviewComment, ReviewCommentStatus } from './types';

type Filter = 'open' | 'queued' | 'sent';

interface Props {
  conversationId: string;
  onJumpToQuestion: (index: number) => void;
}

export function AssessmentReviewsPanel({ conversationId, onJumpToQuestion }: Props) {
  const { reviews, loading, error, refresh, updateCommentStatus, removeReview } = useAssessmentReviews(conversationId, true);
  const enqueue = useReviewQueueStore((s) => s.enqueue);
  const removeFromQueue = useReviewQueueStore((s) => s.remove);
  const queuedIds = useReviewQueueStore((s) => new Set((s.queuesByConversation[conversationId] ?? []).map((q) => q.commentId)));
  const [filter, setFilter] = useState<Filter>('open');

  const counts = useMemo(() => {
    const c = { open: 0, queued: 0, sent: 0 };
    for (const r of reviews) for (const cm of r.comments) {
      if (cm.status === 'pending') c.open++;
      else if (cm.status === 'accepted') c.queued++;
      else if (cm.status === 'sent') c.sent++;
    }
    return c;
  }, [reviews]);

  const filtered = useMemo(() => filterReviews(reviews, filter), [reviews, filter]);

  const handleAddToChat = async (review: AssessmentOwnerReview, comment: AssessmentOwnerReviewComment) => {
    enqueue(conversationId, {
      reviewId: review.id,
      reviewerName: review.reviewerName,
      commentId: comment.id,
      text: comment.text,
      anchor: {
        kind: 'question',
        questionId: comment.questionId,
        questionIndex: comment.questionIndex,
        questionStem: comment.questionStem,
        format: comment.format,
      },
    });
    await updateCommentStatus(review.id, comment.id, { status: 'accepted' });
  };

  const handleRemoveFromChat = (review: AssessmentOwnerReview, commentId: string) => {
    removeFromQueue(conversationId, commentId);
    updateCommentStatus(review.id, commentId, { status: 'pending' });
  };

  const handleSetStatus = (review: AssessmentOwnerReview, commentId: string, status: ReviewCommentStatus) => {
    if (status !== 'accepted' && queuedIds.has(commentId)) removeFromQueue(conversationId, commentId);
    updateCommentStatus(review.id, commentId, { status });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 pb-2 flex items-center gap-1.5 flex-shrink-0">
        <button onClick={refresh} title="Refresh" disabled={loading}
          className={cn('w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5', loading && 'animate-spin')}>
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        {reviews.length > 0 && <>
          <FilterPill active={filter === 'open'} onClick={() => setFilter('open')} label="Open" count={counts.open} accent="amber" />
          <FilterPill active={filter === 'queued'} onClick={() => setFilter('queued')} label="Queued" count={counts.queued} accent="blue" />
          <FilterPill active={filter === 'sent'} onClick={() => setFilter('sent')} label="Sent" count={counts.sent} accent="emerald" />
        </>}
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && <div className="m-3 p-3 rounded-md text-xs bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300">{error}</div>}
        {!loading && reviews.length === 0 && !error && <EmptyState />}
        {reviews.length > 0 && filtered.length === 0 && <div className="px-6 py-10 text-center text-[12px] studio-text-secondary">Nothing here. Try another filter.</div>}
        <div className="px-3 pb-4 space-y-2">
          {filtered.map((r) => (
            <ReviewCard key={r.id} review={r} queuedIds={queuedIds}
              onJump={onJumpToQuestion} onAddToChat={handleAddToChat}
              onRemoveFromChat={handleRemoveFromChat} onSetStatus={handleSetStatus} onDelete={removeReview} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ review, queuedIds, onJump, onAddToChat, onRemoveFromChat, onSetStatus, onDelete }: {
  review: AssessmentOwnerReview;
  queuedIds: Set<string>;
  onJump: (index: number) => void;
  onAddToChat: (review: AssessmentOwnerReview, comment: AssessmentOwnerReviewComment) => void;
  onRemoveFromChat: (review: AssessmentOwnerReview, commentId: string) => void;
  onSetStatus: (review: AssessmentOwnerReview, commentId: string, status: ReviewCommentStatus) => void;
  onDelete: (reviewId: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const total = review.comments.length;
  return (
    <div className="rounded-lg border border-black/5 dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.02] overflow-hidden">
      <div className="flex items-center gap-2.5 px-3 py-2">
        <ReviewerAvatar name={review.reviewerName} />
        <button onClick={() => setOpen((v) => !v)} className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-[13px] font-semibold studio-text-primary truncate">{review.reviewerName}</span>
              <span className="text-[11px] studio-text-secondary flex-shrink-0">{relativeTime(review.submittedAt)}</span>
            </div>
            <span className="text-[11px] studio-text-secondary">{total} comment{total === 1 ? '' : 's'}</span>
          </div>
          <ChevronDown className={cn('h-4 w-4 studio-text-secondary transition-transform flex-shrink-0', open && 'rotate-180')} />
        </button>
        <button onClick={() => onDelete(review.id)} title="Delete review"
          className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-rose-500/10 hover:text-rose-600 studio-text-secondary">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }} className="border-t border-black/5 dark:border-white/[0.05] overflow-hidden">
            <div className="py-1">
              {review.comments.map((c) => (
                <CommentRow key={c.id} comment={c} isQueued={queuedIds.has(c.id)}
                  onJump={() => onJump(c.questionIndex)}
                  onAddToChat={() => onAddToChat(review, c)}
                  onRemoveFromChat={() => onRemoveFromChat(review, c.id)}
                  onSetStatus={(s) => onSetStatus(review, c.id, s)} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CommentRow({ comment, isQueued, onJump, onAddToChat, onRemoveFromChat, onSetStatus }: {
  comment: AssessmentOwnerReviewComment;
  isQueued: boolean;
  onJump: () => void;
  onAddToChat: () => void;
  onRemoveFromChat: () => void;
  onSetStatus: (status: ReviewCommentStatus) => void;
}) {
  const dismissed = comment.status === 'dismissed';
  const sent = comment.status === 'sent';
  return (
    <div className={cn('group relative px-3 py-2.5 rounded-md transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]', dismissed && 'opacity-50')}>
      <button onClick={onJump} title="Jump to this question"
        className="inline-flex items-center gap-1.5 text-[11px] mb-1.5 px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 studio-text-secondary hover:studio-text-primary transition-colors">
        <span className="font-medium">Q{comment.questionIndex + 1}</span>
        {comment.format && <span className="opacity-70">· {comment.format}</span>}
        {comment.questionStem && <span className="opacity-70 max-w-[140px] truncate">· {comment.questionStem}</span>}
      </button>
      <p className={cn('text-[13px] studio-text-primary whitespace-pre-wrap break-words leading-relaxed', dismissed && 'line-through')}>{comment.text}</p>
      {(sent || isQueued) && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold">
          {sent && <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="h-3 w-3" /> Sent to Ivy</span>}
          {isQueued && !sent && <span className="inline-flex items-center gap-1 text-primary dark:text-accent-foreground"><MessageSquarePlus className="h-3 w-3" /> Queued in chat</span>}
        </div>
      )}
      <div className={cn('absolute right-2 top-2 flex items-center gap-0.5', isQueued ? 'opacity-100' : 'opacity-0 group-hover:opacity-100', 'transition-opacity')}>
        {!sent && !dismissed && (isQueued ? (
          <button onClick={onRemoveFromChat} title="Move back to Open"
            className="h-6 px-1.5 rounded text-[11px] inline-flex items-center gap-1 bg-primary/15 hover:bg-primary/25 text-accent-foreground"><X className="h-3 w-3" />Move back</button>
        ) : (
          <button onClick={onAddToChat} title="Add to Ivy chat"
            className="h-6 px-1.5 rounded text-[11px] inline-flex items-center gap-1 bg-black/[0.06] hover:bg-black/[0.12] dark:bg-white/[0.08] dark:hover:bg-white/[0.16] studio-text-primary"><MessageSquarePlus className="h-3 w-3" />Add to chat</button>
        ))}
        {!sent && (dismissed
          ? <button onClick={() => onSetStatus('pending')} title="Restore" className="h-6 w-6 rounded inline-flex items-center justify-center hover:bg-black/[0.08] dark:hover:bg-white/[0.10] studio-text-secondary"><Check className="h-3.5 w-3.5" /></button>
          : <button onClick={() => onSetStatus('dismissed')} title="Dismiss" className="h-6 w-6 rounded inline-flex items-center justify-center hover:bg-black/[0.08] dark:hover:bg-white/[0.10] studio-text-secondary"><Trash2 className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, count, accent }: { active: boolean; onClick: () => void; label: string; count: number; accent: 'amber' | 'emerald' | 'blue' }) {
  return (
    <button onClick={onClick}
      className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors',
        active ? 'bg-black/[0.08] dark:bg-white/[0.12] studio-text-primary' : 'studio-text-secondary hover:bg-black/[0.04] dark:hover:bg-white/[0.06]')}>
      {label}
      <span className={cn('inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] tabular-nums',
        accent === 'amber' && 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
        accent === 'blue' && 'bg-primary/20 text-accent-foreground',
        accent === 'emerald' && 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300')}>{count}</span>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto w-10 h-10 rounded-full bg-black/[0.04] dark:bg-white/[0.05] flex items-center justify-center mb-3"><Inbox className="w-5 h-5 studio-text-secondary" /></div>
      <p className="text-[13px] font-medium studio-text-primary">No reviews yet</p>
      <p className="text-[11px] studio-text-secondary mt-1 leading-relaxed">
        Enable reviews on a share link, then open it with <code className="px-1 py-0.5 rounded bg-black/[0.06] dark:bg-white/10 font-mono text-[10px]">?review=1</code> to invite per-question feedback.
      </p>
    </div>
  );
}

function filterReviews(reviews: AssessmentOwnerReview[], filter: Filter): AssessmentOwnerReview[] {
  return reviews
    .map((r) => ({
      ...r,
      comments: r.comments.filter((c) =>
        filter === 'open' ? c.status === 'pending' : filter === 'queued' ? c.status === 'accepted' : c.status === 'sent'),
    }))
    .filter((r) => r.comments.length > 0);
}
